import { supabase } from '../supabaseClient';

export interface VerificationRequest {
  id: string;
  user_id: string;
  verification_code: string;
  linkedin_url?: string;
  website_url?: string;
  portfolio_url?: string;
  verification_photo_url: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  rejection_reason?: string;
  admin_notes?: string;
  submitted_at: string;
  reviewed_at?: string;
  reviewed_by?: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export interface VerificationRequestWithUser extends VerificationRequest {
  user_name: string;
  user_email: string;
  reviewed_by_name?: string;
}

export interface CreateVerificationRequestData {
  verification_photo_url: string;
  linkedin_url?: string;
  website_url?: string;
  portfolio_url?: string;
}

export interface VerificationStats {
  total_requests: number;
  pending_requests: number;
  approved_requests: number;
  rejected_requests: number;
  expired_requests: number;
}

// Create verification request
export const createVerificationRequest = async (
  data: CreateVerificationRequestData
): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Check if user already has a pending verification request
    const { data: existingRequest, error: checkError } = await supabase
      .from('verification_requests')
      .select('id, status')
      .eq('user_id', user.id)
      .eq('status', 'PENDING')
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking existing request:', checkError);
      return { success: false, error: 'Error checking existing verification request' };
    }

    if (existingRequest) {
      return { success: false, error: 'You already have a pending verification request' };
    }

    // Call the database function
    const { data: result, error } = await supabase.rpc('create_verification_request', {
      p_user_id: user.id,
      p_verification_photo_url: data.verification_photo_url,
      p_linkedin_url: data.linkedin_url || null,
      p_website_url: data.website_url || null,
      p_portfolio_url: data.portfolio_url || null
    });

    if (error) {
      console.error('Error creating verification request:', error);
      return { success: false, error: error.message };
    }

    if (!result.success) {
      return { success: false, error: result.error || 'Failed to create verification request' };
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Error creating verification request:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Get user's verification requests
export const getUserVerificationRequests = async (): Promise<{ 
  data: VerificationRequest[]; 
  error: string | null 
}> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: [], error: 'Not authenticated' };
    }

    const { data, error } = await supabase
      .from('verification_requests')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching verification requests:', error);
      return { data: [], error: error.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching verification requests:', error);
    return { 
      data: [], 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Get verification requests for admin
export const getVerificationRequests = async (
  status?: string,
  limit: number = 20,
  offset: number = 0
): Promise<{ 
  data: VerificationRequestWithUser[]; 
  error: string | null 
}> => {
  try {
    const { data, error } = await supabase.rpc('get_verification_requests', {
      p_status: status || null,
      p_limit: limit,
      p_offset: offset
    });

    if (error) {
      console.error('Error fetching verification requests:', error);
      return { data: [], error: error.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching verification requests:', error);
    return { 
      data: [], 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Approve verification request
export const approveVerification = async (
  requestId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data: result, error } = await supabase.rpc('approve_verification', {
      p_admin_id: user.id,
      p_verification_id: requestId
    });

    if (error) {
      console.error('Error approving verification:', error);
      return { success: false, error: error.message };
    }

    if (!result.success) {
      return { success: false, error: result.error || 'Failed to approve verification' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error approving verification:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Reject verification request
export const rejectVerification = async (
  requestId: string,
  rejectionReason: string,
  adminNotes?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data: result, error } = await supabase.rpc('reject_verification', {
      p_admin_id: user.id,
      p_verification_id: requestId,
      p_rejection_reason: rejectionReason,
      p_admin_notes: adminNotes || null
    });

    if (error) {
      console.error('Error rejecting verification:', error);
      return { success: false, error: error.message };
    }

    if (!result.success) {
      return { success: false, error: result.error || 'Failed to reject verification' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error rejecting verification:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Get verification stats for admin
export const getVerificationStats = async (): Promise<{ 
  data: VerificationStats | null; 
  error: string | null 
}> => {
  try {
    const { data, error } = await supabase
      .from('verification_requests')
      .select('status');

    if (error) {
      console.error('Error fetching verification stats:', error);
      return { data: null, error: error.message };
    }

    const stats: VerificationStats = {
      total_requests: data.length,
      pending_requests: data.filter(r => r.status === 'PENDING').length,
      approved_requests: data.filter(r => r.status === 'APPROVED').length,
      rejected_requests: data.filter(r => r.status === 'REJECTED').length,
      expired_requests: data.filter(r => r.status === 'EXPIRED').length
    };

    return { data: stats, error: null };
  } catch (error) {
    console.error('Error fetching verification stats:', error);
    return { 
      data: null, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Check if user can create verification request
export const canCreateVerificationRequest = async (): Promise<{ 
  canCreate: boolean; 
  error?: string 
}> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { canCreate: false, error: 'Not authenticated' };
    }

    // Check if user is a freelancer
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('role, verification_status')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.error('Error fetching profile:', profileError);
      return { canCreate: false, error: 'Error fetching profile' };
    }

    if (!profile) {
      return { canCreate: false, error: 'Profile not found' };
    }

    // Only freelancers can create verification requests
    if (profile.role !== 'FREELANCER') {
      return { canCreate: false, error: 'Only freelancers can request verification' };
    }

    // Check if user already has a pending verification request
    const { data: existingRequest, error: checkError } = await supabase
      .from('verification_requests')
      .select('id, status')
      .eq('user_id', user.id)
      .eq('status', 'PENDING')
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking existing request:', checkError);
      return { canCreate: false, error: 'Error checking existing verification request' };
    }

    if (existingRequest) {
      return { canCreate: false, error: 'You already have a pending verification request' };
    }

    // Check if user is already verified
    if (profile.verification_status === 'VERIFIED') {
      return { canCreate: false, error: 'You are already verified' };
    }

    return { canCreate: true };
  } catch (error) {
    console.error('Error checking verification eligibility:', error);
    return { 
      canCreate: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};
